//================================================================
//  Component: Vendor Risk Pane 
//================================================================

//  Purpose: This side pane allows the user to request a vendor risk rating from UpGuard

//  Properties:
//    - paneOpen = { useState, holds a boolean value that opens and closes the sidePane}
//    - setPaneOpen = { useState, updates the boolean value that opens and closes the sidePane}
//    - task = { useState, store the related parent demand management task}

//  Example:
//  
//    <VendorRiskPane
//      paneOpen={paneOpen}
//      setPaneOpen={setPaneOpen}
//      task={task}
//    ></VendorRiskPane>

//================================================================

//Libraries
import React, { useContext, useState } from 'react';

//Contexts
import { GetUser } from '../../../../../Library/GlobalContexts';

//Components

//Functions

//Images
import Loading from '../../../../../Components/Images/Image_Loading_Ripple.svg';
import WriteDocument from '../../../../../Library/WriteDocument';

//CSS

export default function VendorRiskPane({
  paneOpen,
  setPaneOpen,
  task,
}) {

  //------------------------------------------------------
  //  useContexts
  //------------------------------------------------------

  const getUser = useContext(GetUser);

  //------------------------------------------------------
  //  useState
  //------------------------------------------------------

  // Used to set pane status > 'onload', 'pending', 'error'
  const [paneStatus, setPaneStatus] = useState('onload');

  // used to store domain name
  const [domainName, setDomainName] = useState('');

  //------------------------------------------------------
  //  Functions
  //------------------------------------------------------

  // Submit Handler 
  const onSubmit = () => {

    setPaneStatus('pending');

    const vendorData = {
      'taskname': 'Vendor Risk Rating',
      'domainname': domainName,
      'createddate': new Date(),
      'createdbyemail': getUser?.emailaddress,
      'createdbygivenname': getUser?.givenname,
      'createdbysurname': getUser?.surname,
      'overallrating': {
        'grade': '',
        'score': '',
      },
      'automatedrating': {
        'grade': '',
        'score': '',
      },
      'questionnairerating': {
        'grade': '',
        'score': '',
      },
      'status': 'In Progress',
      'errormessage': ''
    }

    // Ensure vendorriskratings is initialized as an array if it doesn't exist
    if (!task?.vendorriskratings) {
      task.vendorriskratings = [];
    }

    task?.vendorriskratings.push(vendorData);

    WriteDocument('requests', task?.requestid, task).then(() => {

      setPaneStatus('onload');
      setPaneOpen({ 'vendorrisk': false });
      setDomainName('');

    }).catch((error) => {

      console.log('error', error);
      setPaneStatus('error');

    });

  }

  //------------------------------------------------------
  //  HTML
  //------------------------------------------------------

  // ==================================================
  //  Hide Side Pane
  // ==================================================

  if (!paneOpen?.vendorrisk) return null;

  // ---------------------------------------------------
  //  Onload
  // ---------------------------------------------------

  if (paneStatus === 'onload') {

    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container'>

          {/* Form */}
          <form className='grid grid-rows-[80px_1fr_70px] items-center justify-between w-full overflow-hidden' onSubmit={onSubmit}>

            {/* Header */}
            <div className='flex flex-col gap-2 p-4'>
              <h4 className='text-[22px] p-0 m-0'> Generate Vendor Risk Rating</h4>
            </div>

            {/* Body */}
            <div className='w-[50vw] h-full flex flex-col overflow-y-auto p-4 border border-solid shadow-inner'>

              <label className='font-medium mb-3'> Domain Name <span className='text-[#C4314B]'>*</span> </label>

              <div className='flex items-center border-solid border-1 border-[#ccc] rounded-[4px] p-2'>
                <span className='mr-[5px] text-[#888]'>
                  https://www.
                </span>
                <input className='border-none outline-none flex-1 text-base' type='text' value={domainName} onChange={(e) => setDomainName(e.target.value)}></input>
              </div>

            </div>

            {/* Footer */}
            <div className='px-4 py-3'>
              <div className='flex flex-row gap-2'>

                {/* Create */}
                <button className='Primary-Button' type='submit' value='submit' disabled={domainName?.length === 0}>
                  Create
                </button>

                {/* Cancel */}
                <button className='Secondary-Button' onClick={() => {

                  // Reset Form and Close Side Pane
                  setPaneOpen({ 'vendorrisk': false });
                  setDomainName('');

                }}>
                  Cancel
                </button>

              </div>
            </div>

          </form>

        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Pending
  // ---------------------------------------------------

  else if (paneStatus === 'pending') {
    return (
      <div className='Pane-Background'>
        <dialog className='Pane-Container flex justify-center items-center text-center'>
          <img alt='loading-circle-icon' src={Loading}></img>
        </dialog>
      </div>
    )
  }

  // ---------------------------------------------------
  //  Error
  // ---------------------------------------------------

  else if (paneStatus === 'error') {
    return (
      <div className='Pane-Background'>

        {/* Pane Container */}
        <dialog className='Pane-Container'>
          <div className='flex flex-col gap-2 w-full justify-center items-center'>

            <img className='my-2 w-[100px]' src={Error} alt='Error'></img>
            <h4 className='mb-0'>Oops! Something went wrong.</h4>
            <p className='text-center leading-[1.7]'>
              An error occurred while we processed your request.
            </p>
            <button
              className='Primary-Button'
              onClick={() => {

                // Reset Form and Close Pane
                setPaneOpen({ 'vendorrisk': false });
              }}
            >
              Close
            </button>

          </div>
        </dialog>
      </div>

    )
  }

  //------------------------------------------------------

}